(ns practitest.frontend.tests.test-form.call-to-test
  (:require [ajax.core :as ajax]
            [clojure.set :as set]
            [practitest.frontend.common.dom :as dom]
            [practitest.frontend.common.i18n :refer [format-translation get-translation]]
            [practitest.frontend.common.re-frame :refer [partial-dispatch partial-subscribe]]
            [practitest.frontend.components.grid.events :as grid.events]
            [practitest.frontend.components.grid.subs :as grid.subs]
            [practitest.frontend.components.icons :as icons]
            [practitest.frontend.components.modal :as modal]
            [practitest.frontend.components2.add-entities :as add-entities]
            [practitest.frontend.components2.buttons :as buttons]
            [practitest.frontend.components2.grid :as grid]
            [practitest.frontend.components2.spinners :as spinners]
            [practitest.frontend.routing.backend-routes :as broutes]
            [practitest.frontend.subs :as subs]
            [practitest.frontend.tests.test-form.common :refer [get-last-position
                                                                insert-steps-at
                                                                init-step-form-object
                                                                random-step-id
                                                                steps-form-id]]
            [practitest.frontend.tests.test-form.tests-grid :as tests-grid]
            [re-frame.core :as re-frame]
            [reagent.core :as reagent]))

(def call-to-test-form-id :call-to-test)

;;; Helpers

(defn- selection-disabled-reason [parent-test-id row]
  (cond
    (not= "ScriptedTest" (:test_type row))
    :not-scripted
    (= parent-test-id (:id row))
    :recursion
    :else
    nil))

(defn get-common-label-names
  [caller-labels callee-labels]
  (when (and (seq caller-labels) (seq callee-labels))
    (let [callee-label-ids (set (map :id callee-labels))
          common-labels (filter #(callee-label-ids (:id %)) caller-labels)]
      (when (seq common-labels)
        (clojure.string/join ", " (map :name common-labels))))))

(defn format-ctt-name
  [callee-test caller-test]
  (when callee-test
    (let [base-name (str "Test #" (:display_id callee-test) ": " (:name callee-test))
          callee-is-current (:current callee-test)]
      (if callee-is-current
        base-name
        (if-let [label-names (get-common-label-names
                               (:version_label caller-test)
                               (:version_label callee-test))]
          [:span base-name " " [:strong "[Labeled " label-names "]"]]
          base-name)))))

;;; Subscriptions

(re-frame/reg-sub
  ::form-state
  (fn [db]
    (get-in db [:form-states2 steps-form-id call-to-test-form-id])))

(re-frame/reg-sub
  ::show-call-to-test?
  :<- [::form-state]
  (fn [form-state]
    (:show? form-state)))

(re-frame/reg-sub
  ::has-selection?
  :<- [::grid.subs/selection [tests-grid/grid-entity-type call-to-test-form-id]]
  (fn [selected-tests]
    (seq selected-tests)))

(re-frame/reg-sub
  ::selection-count
  :<- [::grid.subs/selection [tests-grid/grid-entity-type call-to-test-form-id]]
  (fn [selected-tests]
    (count selected-tests)))

(re-frame/reg-sub
  ::already-called-test-ids
  (fn [db]
    ;; Get list of display_ids of all call-to-test steps on parent form
    (set (map #(:display_id (:callee-test %))
              (filter #(= :call-to-test (:type %))
                      (vals (get-in db [:form-states2 steps-form-id :steps])))))))

(re-frame/reg-sub
  ::add-position
  :<- [::form-state]
  (fn [form-state]
    (:add-position form-state)))

;;; Events

(re-frame/reg-event-db
  ::hide-call-to-test
  (fn [db]
    ;; Reset form state
    (assoc-in db [:form-states2 steps-form-id call-to-test-form-id] {})))

(re-frame/reg-event-db
  ::show-call-to-test
  (fn [db [_ add-position]]
    (update-in db [:form-states2 steps-form-id call-to-test-form-id]
               assoc :show? true :add-position add-position)))

(re-frame/reg-event-fx
  ::toggle-selection
  (fn [{db :db} [_ test-id clear?]]
    ;; Need to save test data in this case
    ;; TODO: refactor common selection implementation to store data instead of IDs ?
    (let [tests-data @(re-frame/subscribe [::tests-grid/tests-grid-data call-to-test-form-id])
          test (first (filter #(= (:id %) test-id) tests-data))]
      {:db (if clear?
             ;; Reset tests data on selection resets as well
             (assoc-in db [:form-states2 steps-form-id call-to-test-form-id :tests-data] {})
             (assoc-in db [:form-states2 steps-form-id call-to-test-form-id :tests-data test-id] test))
       :dispatch [::grid.events/toggle-selection [tests-grid/grid-entity-type call-to-test-form-id] test-id clear?]})))

(re-frame/reg-event-fx
  ::toggle-selection-page
  (fn [{db :db} [_ page-rows]]
    ;; Same reasoning as ::toggle-selection above - need to cache row data for later use in ::add-call-to-test
    {:db (update-in db [:form-states2 steps-form-id call-to-test-form-id :tests-data]
                    merge (zipmap (map :id page-rows) page-rows))
     :dispatch [::grid.events/toggle-selection-page [tests-grid/grid-entity-type call-to-test-form-id] page-rows]}))

(re-frame/reg-event-fx
  ::add-call-to-test
  (fn [{db :db} [_ add-position]]
    (let [selected-test-ids @(re-frame/subscribe [::grid.subs/selection [tests-grid/grid-entity-type call-to-test-form-id]])]
      {:db (-> db
               (update-in [:form-states2 steps-form-id :steps]
                          (fn [steps]
                            (let [add-position (or add-position
                                                   (get-last-position steps))
                                  new-steps (map (fn [new-test-id]
                                                   (let [step-id (random-step-id)
                                                         test (get-in db [:form-states2 steps-form-id call-to-test-form-id :tests-data new-test-id])]
                                                     ;; Position will be recalculated in insert-steps-at
                                                     (init-step-form-object step-id 0 true
                                                                            ;; Mimicking backend structure here
                                                                            {:type :call-to-test
                                                                             :callee_test test})))
                                                 selected-test-ids)]
                              (insert-steps-at steps new-steps add-position))))
               (assoc-in [:form-states2 steps-form-id :dirty?] true))
       :dispatch [::hide-call-to-test]})))

(re-frame/reg-event-db
  ::on-expand-data-fetched
  (fn [db [_ form-id test-id response]]
    (-> db
        (update :progress disj [::tests-grid/fetch-step-data form-id test-id])
        (assoc-in [:form-states2 steps-form-id form-id :steps-data test-id]
                  (:steps response)))))

(re-frame/reg-event-fx
  ::expand-row-and-fetch-data
  (fn [{db :db} [_ form-id test-id]]
    {:db (-> db
             (update-in [:form-states2 steps-form-id form-id :expanded-tests] (fnil conj #{}) test-id)
             (update :progress conj [::tests-grid/fetch-step-data form-id test-id]))
     :http-xhrio
     {:method :get
      :uri (broutes/get-url "qtest_designs#entity"
                            {:p_id (subs/get-current-project-id db)
                             :id test-id})
      :response-format (ajax/json-response-format {:keywords? true})
      :on-success [::on-expand-data-fetched form-id test-id]
      :on-failure [::tests-grid/on-expand-data-error form-id test-id]}}))

(re-frame/reg-event-db
  ::collapse-row
  (fn [db [_ form-id test-id]]
    (update-in db [:form-states2 steps-form-id form-id]
               (fn [form-state]
                 (-> form-state
                     (update :expanded-tests disj test-id)
                     (update :steps-data dissoc test-id))))))

(re-frame/reg-event-fx
  ::toggle-expand-row
  (fn [_ [_ test-row]]
    (let [test-id (:id test-row)
          expanded? @(re-frame/subscribe [::tests-grid/expanded-row? call-to-test-form-id test-row])]
      (if expanded?
        {:dispatch [::collapse-row call-to-test-form-id test-id]}
        {:dispatch [::expand-row-and-fetch-data call-to-test-form-id test-id]}))))

;;; External components

(defn- expandable-row-test-steps [form-id grid-props idx row]
  (when @(re-frame/subscribe [::tests-grid/expanded-row? form-id row])
    (let [test-id (:id row)
          even-row? (grid/apply-even-class? idx)
          columns-count (count (:shown-columns grid-props))
          step-data @(re-frame/subscribe [::tests-grid/steps-data form-id test-id])
          loading? @(re-frame/subscribe [::subs/progress? [::tests-grid/fetch-step-data form-id test-id]])]
      (into
        [:<>
         (when loading?
           [:tr {:class (dom/classes-pairs-v :grid-row-even even-row?)}
            [:td {:col-span columns-count}
             [spinners/mux-spinner {:size :small
                                    :delay 100}]]])]
        (map-indexed
          (fn [idx step]
            [:tr {:class (dom/classes-pairs-v :grid-row-even even-row?)
                  :key (:id step)}
             [:td.hierarchical-child-cell
              [:span.hierarchical-child-icon
               [icons/hierarchical-child]]]
             [:td {:col-span (max 1 columns-count)}
              [:span (str "Step " (inc idx) ": ")]
              [:span (:name step)]]])
          step-data)))))

(defn call-to-test-modal []
  (let [grid-state (reagent/atom {})
        show? (re-frame/subscribe [::show-call-to-test?])
        hide! #(re-frame/dispatch [::hide-call-to-test])]
    (fn [{:keys [main-ref entity-id base-entity]}]
      [modal/bootstrap-modal
       {:is-open? @show?
        :on-request-close hide!
        :container-ref main-ref
        :class "copy-steps-modal"
        :close-button? true}
       ;; header
       [:span (format-translation (get-translation "test.steps.call-to-test.modal-header"
                                                   "Call to Test from %{caller}")
                                  :caller (format-ctt-name base-entity base-entity))]
       ;; body
       [add-entities/add-entities
        (merge
          (tests-grid/default-grid-props call-to-test-form-id nil grid-state)
          {:expandable-row? (partial-subscribe [::tests-grid/expandable-row?])
           :expanded-row? (partial-subscribe [::tests-grid/expanded-row? call-to-test-form-id])
           :expandable-row-component (partial expandable-row-test-steps call-to-test-form-id)
           :on-expand-row-click (fn [e row]
                                  (re-frame/dispatch [::toggle-expand-row row]))
           :disabled-row? (reagent/partial selection-disabled-reason entity-id)
           :disabled-row-tooltip (fn [row]
                                   (let [reason (selection-disabled-reason entity-id row)]
                                     (case reason
                                       :not-scripted
                                       (get-translation "test.steps.call-to-test.not-scripted-tooltip"
                                                        "Can only call scripted tests")
                                       :recursion
                                       (get-translation "test.steps.call-to-test.recursion-tooltip"
                                                        "Cannot call itself in steps")
                                       ;; else
                                       (get-translation "test.steps.call-to-test.disabled-unknown-reason"
                                                        "Cannot call this test"))))
           :toggle-selection (partial-dispatch [::toggle-selection])
           :toggle-selection-page (partial-dispatch [::toggle-selection-page])
           :allows-added-rows? true
           :added-tests @(re-frame/subscribe [::already-called-test-ids])
           :added-row-tooltip (get-translation "test.steps.call-to-test.already-have-this-call-tooltip"
                                               "This test is already called in current test")})]
       ;; footer
       [:div.m-button-group
        (let [added-tests @(re-frame/subscribe [::already-called-test-ids])
              selection @(re-frame/subscribe [::grid.subs/selection [tests-grid/grid-entity-type call-to-test-form-id]])
              tests-data @(re-frame/subscribe [::tests-grid/tests-grid-data call-to-test-form-id])
              ;; Map selected test IDs to their display IDs
              selected-display-ids (set (keep (fn [test]
                                                (when (contains? selection (:id test))
                                                  (:display_id test)))
                                              tests-data))]
          (when (some #(contains? added-tests %) selected-display-ids)
            [:div
             [:strong (get-translation "generic.warning.note" "Note: ")]
             [:span (get-translation "test.steps.call-to-test.already-have-this-call-warning"
                                     "you selected tests that are already called in your test")]]))
        [buttons/button
         {:text (get-translation "generic.button.cancel" "Cancel")
          :on-click hide!}]
        [buttons/button
         {:primary? true
          :disabled? (not @(re-frame/subscribe [::has-selection?]))
          :text (format-translation
                  (get-translation "test.steps.call-to-test.add-selected-tests-count"
                                   "Add selected Tests (%{count})")
                  :count @(re-frame/subscribe [::selection-count]))
          :icon [icons/mux-plus-circle]
          :on-click (fn [e]
                      (re-frame/dispatch [::add-call-to-test @(re-frame/subscribe [::add-position])]))}]]])))

